import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, ExternalLink, AlertOctagon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useApp } from '@/contexts/AppContext';
import { TRUST_URL } from '@/components/apex/Sidebar';
import LandingNav from '@/components/landing/LandingNav';
import AccessRequestModal, { useAccessGate } from '@/components/landing/AccessRequestModal';
import ApexHeroLogo from '@/components/landing/ApexHeroLogo';
import LandingFooter from '@/components/landing/LandingFooter';
import PageMeta from '@/components/landing/PageMeta';

const BG = '#06070A';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function Home() {
  const { seed } = useApp();
  const ps = seed?.platform_status;
  const navigate = useNavigate();
  const gateOpen = useAccessGate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [modal, setModal] = useState({ open: false, product: 'Apex Lens' });
  const [heroWidth, setHeroWidth] = useState(480);

  // Responsive hero logo width per brand spec:
  // desktop 420–520, tablet 320–380, mobile 220–260.
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w < 640) setHeroWidth(240);
      else if (w < 1024) setHeroWidth(360);
      else setHeroWidth(480);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  useEffect(() => {
    const gate = searchParams.get('gate');
    if ((gate === 'lens' || gate === 'demo') && !gateOpen) {
      setModal({ open: true, product: 'Apex Lens' });
    }
  }, [searchParams, gateOpen]);

  const requestLens = useCallback(() => {
    if (gateOpen) { navigate('/lens'); return; }
    setModal({ open: true, product: 'Apex Lens' });
  }, [gateOpen, navigate]);

  const requestTrust = useCallback(() => {
    if (gateOpen) { window.open(TRUST_URL, '_blank', 'noopener,noreferrer'); return; }
    setModal({ open: true, product: 'Apex Trust' });
  }, [gateOpen]);

  function onGranted() {
    setModal({ open: false, product: modal.product });
    if (searchParams.get('gate')) {
      const next = new URLSearchParams(searchParams);
      next.delete('gate');
      const from = next.get('from');
      next.delete('from');
      setSearchParams(next, { replace: true });
      if (modal.product === 'Apex Lens' && from) {
        try {
          const decoded = decodeURIComponent(from);
          if (decoded.startsWith('/lens')) { navigate(decoded); return; }
        } catch (err) {
          // Malformed `from` param — log for diagnostics, then fall through
          // to the default Lens redirect below.
          // eslint-disable-next-line no-console
          if (typeof console !== 'undefined') console.warn('[Home] bad from param', err);
        }
      }
    }
    if (modal.product === 'Apex Trust') {
      window.open(TRUST_URL, '_blank', 'noopener,noreferrer');
    } else {
      navigate('/lens');
    }
  }

  return (
    <div style={{ background: BG, minHeight: '100vh', color: '#F1F5F9' }}>
      <PageMeta
        title="APEX Verity — Governance Proof Infrastructure"
        description="Temporal governance proof infrastructure for cloud and AI systems. Know what governed the decision."
        path="/"
      />
      <LandingNav />

      {/* ═══════════════════════════════════════════════════════════════
          HERO — logo dominates the fold. Nothing else above.
          Logo sits at ~40% viewport height. ≥ 1.5x clear space all around.
          ═══════════════════════════════════════════════════════════════ */}
      <section
        data-testid="home-hero"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          paddingTop: 'clamp(96px, 18vh, 220px)',
          paddingBottom: 'clamp(48px, 8vh, 120px)',
          paddingLeft: 24,
          paddingRight: 24,
          position: 'relative',
        }}
      >
        {/* Logo */}
        <motion.div {...fadeUp(0)}>
          <ApexHeroLogo width={heroWidth} />
        </motion.div>

        {/* Clear space — 1.5x logo height between logo and headline */}
        <div style={{ height: 'clamp(60px, 10vh, 120px)' }} aria-hidden />

        {/* Headline */}
        <motion.h1
          {...fadeUp(0.25)}
          data-testid="home-headline"
          style={{
            textAlign: 'center',
            fontSize: 'clamp(30px, 4.4vw, 52px)',
            fontWeight: 500,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            color: '#F1F5F9',
            margin: 0,
            maxWidth: 820,
          }}
        >
          Know what governed the decision.
        </motion.h1>

        {/* Subhead */}
        <motion.p
          {...fadeUp(0.4)}
          data-testid="home-subhead"
          style={{
            textAlign: 'center',
            marginTop: 24,
            marginBottom: 0,
            fontSize: 'clamp(15px, 1.4vw, 19px)',
            lineHeight: 1.65,
            color: '#94A3B8',
            maxWidth: 640,
            fontWeight: 400,
          }}
        >
          Temporal governance proof infrastructure for cloud and AI systems.
        </motion.p>

        {/* CTA row */}
        <motion.div
          {...fadeUp(0.55)}
          style={{
            marginTop: 44,
            display: 'flex',
            gap: 14,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
          data-testid="home-cta-row"
        >
          <button
            onClick={requestLens}
            style={{
              background: 'transparent',
              color: '#F1F5F9',
              border: '1px solid rgba(226,232,240,0.35)',
              padding: '14px 26px',
              borderRadius: 2,
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              cursor: 'pointer',
              transition: 'background 0.3s ease, border-color 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(226,232,240,0.05)';
              e.currentTarget.style.borderColor = 'rgba(226,232,240,0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'rgba(226,232,240,0.35)';
            }}
            data-testid="home-request-access-btn"
          >
            Request access <ArrowRight size={14} strokeWidth={1.5} />
          </button>
          <Link
            to="/lens/how-it-works"
            style={{
              background: 'transparent',
              color: '#94A3B8',
              border: '1px solid transparent',
              padding: '14px 26px',
              borderRadius: 2,
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              transition: 'color 0.3s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#F1F5F9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#94A3B8'; }}
            data-testid="home-how-it-works-btn"
          >
            How it works
          </Link>
        </motion.div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          BELOW THE FOLD — product cards and platform context.
          User must scroll past the hero fold to see this.
          ═══════════════════════════════════════════════════════════════ */}
      <section
        style={{
          maxWidth: 1100,
          margin: '0 auto',
          padding: '40px 24px 80px',
          position: 'relative',
        }}
      >
        {/* Live status strip — quiet, mono, single-line */}
        <div
          data-testid="home-status-bar"
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 28,
            flexWrap: 'wrap',
            borderTop: '1px solid #14171F',
            borderBottom: '1px solid #14171F',
            padding: '14px 0',
            marginBottom: 64,
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 11,
            color: '#64748B',
            letterSpacing: '0.06em',
          }}
        >
          <span>
            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#10B981', marginRight: 8, verticalAlign: 'middle', boxShadow: '0 0 6px rgba(16,185,129,0.6)' }} />
            {ps?.accounts_monitored ?? 47} accounts monitored
          </span>
          <span>{ps?.der_count ?? '47,832'} governed decisions / month</span>
          <span style={{ color: '#D97706' }}>⚠ 1 active investigation</span>
          <span style={{ color: '#475569' }}>Regulators investigate history, not screenshots.</span>
        </div>

        {/* Product cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 }}>
          {/* Lens */}
          <motion.div {...fadeUp(0)} viewport={{ once: true, amount: 0.3 }} data-testid="home-card-lens">
            <div style={{ border: '1px solid #1E2A45', borderTop: '2px solid #0D9488', borderRadius: 0, background: 'rgba(15,22,41,0.6)', padding: 32, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
                <div>
                  <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.01em', color: '#F1F5F9' }}>APEX Lens</h2>
                  <p style={{ color: '#0D9488', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', marginTop: 6, fontFamily: 'JetBrains Mono, monospace' }}>
                    Cloud Governance Proof
                  </p>
                </div>
                <span style={{ fontSize: 9, color: '#0D9488', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace' }}>LIVE</span>
              </div>

              <p style={{ color: '#94A3B8', fontSize: 13, lineHeight: 1.75, marginBottom: 20 }}>
                Proves what your cloud was doing — and reconstructs the exact governance state
                that existed at that moment.
              </p>

              <div style={{ borderTop: '1px solid #1E2A45', paddingTop: 14, marginBottom: 20, fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: '#475569', display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                <span>{ps?.accounts_monitored ?? 47} accounts</span>
                <span>{ps?.active_investigations ?? 1} active</span>
                <span>Evidence {ps?.evidence_readiness ?? 87}%</span>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 'auto' }}>
                <button onClick={requestLens} style={{ background: 'transparent', color: '#F1F5F9', padding: '10px 18px', border: '1px solid rgba(13,148,136,0.6)', fontSize: 12, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} data-testid="home-request-lens">
                  Request access <ArrowRight size={13} strokeWidth={1.5} />
                </button>
                <Link to="/product/lens" style={{ color: '#64748B', padding: '10px 18px', fontSize: 12, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', textDecoration: 'none' }}>
                  Learn more
                </Link>
              </div>

              <button
                onClick={requestLens}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginTop: 18,
                  padding: '10px 14px', borderRadius: 0,
                  background: 'transparent', border: '1px solid rgba(217,119,6,0.25)',
                  color: '#FBBF24', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase',
                  textAlign: 'left', cursor: 'pointer', width: '100%', fontFamily: 'JetBrains Mono, monospace',
                }}
                data-testid="home-active-inv-banner"
              >
                <AlertOctagon size={13} strokeWidth={1.5} />
                INV-2026-042 requires attention
                <ArrowRight size={11} strokeWidth={1.5} style={{ marginLeft: 'auto' }} />
              </button>
            </div>
          </motion.div>

          {/* Trust */}
          <motion.div {...fadeUp(0.1)} viewport={{ once: true, amount: 0.3 }} data-testid="home-card-trust">
            <div style={{ border: '1px solid #1E2A45', borderTop: '2px solid #6366F1', borderRadius: 0, background: 'rgba(15,22,41,0.6)', padding: 32, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
                <div>
                  <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.01em', color: '#F1F5F9' }}>APEX Trust</h2>
                  <p style={{ color: '#6366F1', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', marginTop: 6, fontFamily: 'JetBrains Mono, monospace' }}>
                    Temporal AI Governance Proof
                  </p>
                </div>
                <span style={{ fontSize: 9, color: '#6366F1', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace' }}>EXTERNAL</span>
              </div>

              <p style={{ color: '#94A3B8', fontSize: 13, lineHeight: 1.75, marginBottom: 20 }}>
                Proves what governed every AI decision — and reconstructs the governance state
                that existed at that exact moment.
              </p>

              <div style={{ borderTop: '1px solid #1E2A45', paddingTop: 14, marginBottom: 20, fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: '#475569', display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                <span>47,832 decisions/mo</span>
                <span>5 governed workflows</span>
                <span>◆ AUDIT-GRADE</span>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 'auto' }}>
                <button onClick={requestTrust} style={{ background: 'transparent', color: '#F1F5F9', padding: '10px 18px', border: '1px solid rgba(99,102,241,0.6)', fontSize: 12, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} data-testid="home-request-trust">
                  Request access <ExternalLink size={13} strokeWidth={1.5} />
                </button>
                <Link to="/product/trust" style={{ color: '#64748B', padding: '10px 18px', fontSize: 12, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', textDecoration: 'none' }}>
                  Learn more
                </Link>
              </div>

              <p
                style={{
                  fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: '#475569',
                  marginTop: 18, letterSpacing: '0.08em', textTransform: 'uppercase',
                }}
                data-testid="home-trust-replay-teaser"
              >
                → Select a date. Watch governance state change.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Managed-like-SaaS motif */}
        <p
          data-testid="home-motif"
          style={{
            textAlign: 'center',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 11,
            color: '#475569',
            letterSpacing: '0.18em',
            marginTop: 64,
            textTransform: 'uppercase',
          }}
        >
          Managed like SaaS — Deployed in your environment.
        </p>
      </section>

      <LandingFooter />

      <AccessRequestModal
        open={modal.open}
        product={modal.product}
        onClose={() => setModal({ open: false, product: modal.product })}
        onGranted={onGranted}
      />
    </div>
  );
}
